import React from "react";
import ColorInput from './ColorInput';
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { auth, storage } from "../firebaseConfig"; // same source you use elsewhere
import "./BackgroundPicker.css";

const extractUrlFromCssBackgroundImage = (bgImage) => {
    if (!bgImage || typeof bgImage !== "string") return "";
    const match = bgImage.match(/url\((['"]?)(.*?)\1\)/i);
    return match?.[2] || "";
};

const normalizeImageUrl = (value) => {
    if (!value || typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^url\(/i.test(trimmed)) return extractUrlFromCssBackgroundImage(trimmed);
    return trimmed;
};

/**
 * Page Background Picker (Workbench-friendly, upload-on-save)
 *
 * Shape:
 *   - { type: 'single', color: '#ffffff' }
 *   - { type: 'gradient', colors: ['#fff','#000'], direction: 'to right' }
 *   - { type: 'image', imageUrl: 'https://...', sizing: 'cover', position: 'center',
 *       repeatMode: 'no-repeat', imageFile?: File }
 *
 * Props:
 *   - current              : background object above
 *   - onBackgroundChange   : (config) => void    // live preview (instant)
 *   - onSave               : async (config) => void  // persist to Firestore (MuultiPage handles it)
 *   - onCancel             : () => void
 *   - onDone               : () => void          // close the workbench
 */
export default function BackgroundPicker({
                                             current = { type: "single", color: "#ffffff" },
                                             onBackgroundChange,
                                             onSave,
                                             ensureAuthUid,
                                             onError,
                                             onCancel,
                                         onDone,
                                     }) {
    // normalize
    const imageUrl =
        normalizeImageUrl(current?.imageUrl) ||
        normalizeImageUrl(current?.url) ||
        normalizeImageUrl(current?.image) ||
        normalizeImageUrl(current?.src) ||
        extractUrlFromCssBackgroundImage(current?.backgroundImage) ||
        "";
    const rawType = typeof current?.type === "string" ? current.type.toLowerCase() : "";
    const normalizedType = rawType === "cover" ? "image" : rawType;
    const type = normalizedType || (imageUrl ? "image" : "single");

    // single
    const singleColor = current?.color ?? current?.backgroundColor ?? "#ffffff";

    // gradient
    const gradColors =
        Array.isArray(current?.colors) && current.colors.length >= 2
            ? current.colors
            : ["#7bc2b6", "#1d3f43"];
    const gradDir = current?.direction ?? "to right";

    // image
    const imageFile = current?.imageFile || null; // pending file
    const sizing = current?.sizing ?? current?.backgroundSize ?? "cover";
    const position = current?.position ?? current?.backgroundPosition ?? "center";
    const repeatMode = current?.repeatMode ?? current?.backgroundRepeat ?? "no-repeat";

    // convenience
    const setCfg = (cfg) => onBackgroundChange?.(cfg);

    const handleSelectType = (nextType) => {
        if (nextType === "single") {
            setCfg({ type: "single", color: singleColor });
        } else if (nextType === "gradient") {
            setCfg({ type: "gradient", colors: gradColors, direction: gradDir });
        } else if (nextType === "image") {
            setCfg({
                type: "image",
                imageUrl,
                imageFile: null,
                sizing,
                position,
                repeatMode,
            });
        } else {
            setCfg({ type: "none" });
        }
    };

    // choose a local file → use blob URL for immediate live preview,
    // stash the File in the model so Save can upload it
    const handlePickImage = (file) => {
        if (!file) return;
        const blobUrl = URL.createObjectURL(file);
        setCfg({
            type: "image",
            imageUrl: blobUrl, // live preview immediately
            imageFile: file,   // cache to upload on Save
            sizing,
            position,
            repeatMode,
        });
    };

    const handleSave = async () => {
        try {
            let finalCfg;

            if (type === "single") {
                finalCfg = { type: "single", color: singleColor };
            } else if (type === "gradient") {
                finalCfg = { type: "gradient", colors: gradColors, direction: gradDir };
            } else if (type === "image") {
                // If we have a picked file (blob URL), upload it now
                if (imageFile && imageUrl && imageUrl.startsWith("blob:")) {
                    const uid = typeof ensureAuthUid === "function"
                        ? await ensureAuthUid()
                        : auth?.currentUser?.uid;
                    if (!uid) throw new Error("User not authenticated for upload.");
                    if (!auth?.currentUser || auth.currentUser.uid !== uid) {
                        throw new Error("Auth UID mismatch for background upload.");
                    }
                    await auth.currentUser.getIdToken(true);

                    // Path: keep it consistent with your other uploads.
                    // You used "componentBackgrounds" and "headerBackgrounds" elsewhere.
                    // We'll use "pageBackgrounds/<uid>/..." for page backgrounds.
                    const path = `backgrounds/${uid}/${Date.now()}_${imageFile.name}`;
                    const sRef = ref(storage, path);
                    await new Promise((res, rej) => {
                        const task = uploadBytesResumable(sRef, imageFile);
                        task.on("state_changed", null, rej, res);
                    });
                    const realUrl = await getDownloadURL(sRef);

                    finalCfg = {
                        type: "image",
                        imageUrl: realUrl, // persisted URL
                        sizing,
                        position,
                        repeatMode,
                    };
                } else {
                    // Nothing new to upload; keep current URL
                    finalCfg = {
                        type: "image",
                        imageUrl,
                        sizing,
                        position,
                        repeatMode,
                    };
                }
            } else {
                finalCfg = { type: "none" };
            }

            if (onSave) {
                await onSave(finalCfg);
            } else {
                onBackgroundChange?.(finalCfg);
            }
            onDone?.();
        } catch (err) {
            console.error("Background save failed:", err);
            onError?.(err?.message || "Background save failed.");
        }
    };

    const handleCancel = () => {
        onCancel?.();
        onDone?.();
    };

    return (
        <div className="background-editor-container">
            {/* Type selector */}
            <div className="form-row">
                <label>Background Type</label>
                <div className="segmented">
                    {["none", "single", "gradient", "image"].map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            className={`segmented__btn ${type === opt ? "is-active" : ""}`}
                            onClick={() => handleSelectType(opt)}
                        >
                            {opt}
                        </button>
                    ))}
                </div>
            </div>

            {/* Single color */}
            {type === "single" && (
                <div className="form-row">
                    <label>Color</label>
                    <ColorInput
                        value={singleColor}
                        onChange={(e) => setCfg({ type: "single", color: e.target.value })}
                    />
                </div>
            )}

            {/* Gradient */}
            {type === "gradient" && (
                <>
                    <div className="form-row">
                        <label>From</label>
                        <ColorInput
                            value={gradColors[0]}
                            onChange={(e) =>
                                setCfg({
                                    type: "gradient",
                                    colors: [e.target.value, gradColors[1]],
                                    direction: gradDir,
                                })
                            }
                        />
                    </div>
                    <div className="form-row">
                        <label>To</label>
                        <ColorInput
                            value={gradColors[1]}
                            onChange={(e) =>
                                setCfg({
                                    type: "gradient",
                                    colors: [gradColors[0], e.target.value],
                                    direction: gradDir,
                                })
                            }
                        />
                    </div>
                    <div className="form-row">
                        <label>Direction</label>
                        <select
                            value={gradDir}
                            onChange={(e) =>
                                setCfg({
                                    type: "gradient",
                                    colors: gradColors,
                                    direction: e.target.value,
                                })
                            }
                        >
                            <option value="to top">to top</option>
                            <option value="to right">to right</option>
                            <option value="to bottom">to bottom</option>
                            <option value="to left">to left</option>
                            <option value="to top right">to top right</option>
                            <option value="to bottom right">to bottom right</option>
                            <option value="to bottom left">to bottom left</option>
                            <option value="to top left">to top left</option>
                        </select>
                    </div>
                </>
            )}

            {/* Image */}
            {type === "image" && (
                <>
                    {/* Current image preview (thumbnail) */}
                    {imageUrl ? (
                        <div className="form-row">
                            <label>Current Image</label>
                            <div className="bg-thumb">
                                {/* Use an <img> so you can see the exact asset that will render. */}
                                <img src={imageUrl} alt="Background" />
                            </div>
                        </div>
                    ) : null}

                    <div className="form-row">
                        <label>Pick Image</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handlePickImage(e.target.files?.[0] || null)}
                        />
                    </div>

                    <div className="form-row">
                        <label>Size</label>
                        <select
                            value={sizing}
                            onChange={(e) =>
                                setCfg({
                                    type: "image",
                                    imageUrl,
                                    imageFile,
                                    sizing: e.target.value,
                                    position,
                                    repeatMode,
                                })
                            }
                        >
                            <option value="cover">cover</option>
                            <option value="contain">contain</option>
                            <option value="auto">auto</option>
                            <option value="100% 100%">100% 100%</option>
                        </select>
                    </div>

                    <div className="form-row">
                        <label>Position</label>
                        <select
                            value={position}
                            onChange={(e) =>
                                setCfg({
                                    type: "image",
                                    imageUrl,
                                    imageFile,
                                    sizing,
                                    position: e.target.value,
                                    repeatMode,
                                })
                            }
                        >
                            <option value="center">center</option>
                            <option value="top">top</option>
                            <option value="bottom">bottom</option>
                            <option value="left">left</option>
                            <option value="right">right</option>
                            <option value="top left">top left</option>
                            <option value="top right">top right</option>
                            <option value="bottom left">bottom left</option>
                            <option value="bottom right">bottom right</option>
                        </select>
                    </div>

                    <div className="form-row">
                        <label>Repeat</label>
                        <select
                            value={repeatMode}
                            onChange={(e) =>
                                setCfg({
                                    type: "image",
                                    imageUrl,
                                    imageFile,
                                    sizing,
                                    position,
                                    repeatMode: e.target.value,
                                })
                            }
                        >
                            <option value="no-repeat">no-repeat</option>
                            <option value="repeat">repeat</option>
                            <option value="repeat-x">repeat-x</option>
                            <option value="repeat-y">repeat-y</option>
                            <option value="space">space</option>
                            <option value="round">round</option>
                        </select>
                    </div>

                </>
            )}

            {/* Actions */}
            <div className="form-actions">
                <button className="muulti-button" onClick={handleSave}>
                    Save
                </button>
                <button className="muulti-button cancel-button" onClick={handleCancel}>
                    Cancel
                </button>
            </div>
        </div>
    );
}
